import { Arrow, Figure, INK_FAINT, INK_MUTED, Lane, Node, Note, Pill } from './primitives';

/**
 * A sweep, and the fix it can lead to — one picture, because the split between them is the point.
 *
 * The upper half writes only to the finding ledger, which is what licenses the whole arrangement: no
 * lease, its own queue, safe to run over the organization on a schedule. The lower half is ordinary
 * ingestion, and it only starts when a person says something.
 */
export function SweepPath() {
  return (
    <Figure
      title="A sweep files cards; a person's instruction turns one into confined ingestion runs"
      width={760}
      height={470}
      caption="A sweep notices; it never edits. Everything above the divider writes to the finding ledger alone — which is why it needs no write lease and can run over the whole organization on a schedule."
    >
      <Note x={0} y={14} lines="START" size={9} weight={700} color={INK_FAINT} />
      <Node x={0} y={26} w={140} h={28} title="Sweep now" tone="accent" />
      <Node x={0} y={62} w={140} h={40} title="daily tick" sub="CAS on lastSweptAt" tone="muted" />

      <Arrow points={[[140, 40], [176, 40], [176, 74]]} />
      <Arrow points={[[140, 82], [176, 82]]} />

      <Node x={188} y={60} w={144} h={44} title="KbSweep" sub="QUEUED" tone="default" />
      <Pill x={196} y={112} label="kb-sweep · 1" tone="muted" />
      <Note x={188} y={146} lines={['one live sweep per scope;', 'an occurrence is consumed,', 'not queued behind one']} size={9} />

      <Arrow points={[[332, 82], [376, 82]]} />

      <Lane x={376} y={16} w={384} h={172} label="the loop · no write lease" tone="accent" />
      <Node x={394} y={38} w={166} h={26} title="5 scoped reads" tone="muted" />
      <Node x={394} y={72} w={166} h={26} title="4 lenses" tone="info" />
      <Node x={394} y={106} w={166} h={26} title="3 ledger tools" tone="accent" />
      <Node x={394} y={140} w={166} h={26} title="sweep_finish" tone="success" mono />

      <Note x={576} y={44} lines={['projects, outline,', 'search, grep, read']} size={9} />
      <Note x={576} y={78} lines={['similar_pairs, neighbours,', 'compare, shared_evidence']} size={9} />
      <Note x={576} y={112} lines={['open_findings, flag,', 'resolve']} size={9} />

      <Arrow points={[[560, 138], [690, 138], [690, 200]]} label="sweep_flag" labelAt={[696, 172]} labelAnchor="start" />

      <Node x={560} y={200} w={200} h={48} title="KbSweepFinding" sub="fingerprint · OPEN" tone="info" />
      <Note
        x={548}
        y={214}
        anchor="end"
        lines={['sha256(kind + sorted nodeIds)', 'same pages → refreshes the card,', 'never a second one']}
        size={9}
      />

      <line x1={0} y1={276} x2={760} y2={276} stroke="var(--color-border)" strokeWidth={1} strokeDasharray="3 5" />
      <Note x={0} y={292} lines="A PERSON ANSWERS A CARD" size={9} weight={700} color={INK_FAINT} />

      <Arrow points={[[660, 248], [660, 264], [660, 306]]} />
      <Node x={560} y={306} w={200} h={40} title="“merge B into A”" tone="accent" />
      <Arrow points={[[560, 326], [500, 326]]} />

      <Node x={276} y={300} w={224} h={52} title="dispatchFindingFix" sub="one run per project touched" tone="default" mono />
      <Arrow points={[[276, 326], [216, 326]]} />
      <Node x={0} y={300} w={216} h={52} title="receive()" sub="USER_FEEDBACK · scopePaths" tone="default" mono />

      <Arrow points={[[108, 352], [108, 384]]} />
      <Node x={0} y={384} w={216} h={44} title="confined write loop" sub="judged like any run" tone="accent" />

      <Arrow points={[[216, 406], [300, 406]]} />
      <Node x={300} y={384} w={200} h={44} title="settleFindingFixRun" sub="waits for the whole batch" tone="default" mono />
      <Arrow points={[[500, 406], [560, 406]]} />
      <Node x={560} y={384} w={200} h={44} title="FIXED, or OPEN again" sub="and an AGENT message" tone="success" />

      <Note x={276} y={368} lines="a MISPLACED move adds a destination run, dispatched alone and first" size={9} color={INK_MUTED} />
    </Figure>
  );
}

/**
 * What a card can be, and what moves it.
 *
 * Worth drawing because two of the transitions are asymmetric in a way a list of statuses hides:
 * a later sweep reopens what it fixed but never argues with a dismissal, and a destination that
 * commits nothing drops the trim rather than half-applying a move.
 */
export function FindingLifecycle() {
  return (
    <Figure
      title="The states a sweep finding moves through"
      width={760}
      height={344}
      minWidth={640}
      caption="RESOLVED and FIXED reopen when a later sweep produces the same fingerprint, because that means the row and the store disagree. DISMISSED does not: it is a person's verdict, and their note is shown to the agent so it does not argue."
    >
      <Node x={286} y={12} w={188} h={32} title="sweep_flag" sub="new fingerprint" tone="accent" mono />
      <Arrow points={[[380, 44], [380, 72]]} />

      {/* The three states a person's verdict produces, across one row. */}
      <Node x={60} y={72} w={160} h={40} title="RESOLVED" tone="success" />
      <Node x={300} y={72} w={160} h={40} title="OPEN" tone="info" />
      <Node x={560} y={72} w={160} h={40} title="DISMISSED" tone="muted" />

      <Arrow points={[[300, 86], [220, 86]]} label="sweep_resolve" labelAt={[260, 80]} />
      <Arrow points={[[460, 86], [560, 86]]} label="a person dismisses" labelAt={[510, 80]} />

      {/* The fix, down the spine. */}
      <Arrow points={[[380, 112], [380, 150]]} label="an instruction" labelAt={[386, 136]} labelAnchor="start" />
      <Node x={300} y={150} w={160} h={40} title="FIXING" tone="accent" />
      <Arrow points={[[380, 190], [380, 222]]} label="runs committed" labelAt={[386, 212]} labelAnchor="start" />
      <Node x={300} y={222} w={160} h={40} title="FIXED" tone="success" />

      {/* Nothing landed: straight back to OPEN, on the right, clear of everything. */}
      <Arrow
        points={[[460, 170], [520, 170], [520, 100], [460, 100]]}
        dashed
        label="nothing committed,"
        labelAt={[526, 136]}
        labelAnchor="start"
      />
      <Note x={526} y={148} lines="or every run failed" />

      {/* Both terminal states reopen the same way, so they share one channel down the left. */}
      <Arrow points={[[140, 112], [140, 300]]} head={false} dashed />
      <Arrow points={[[380, 262], [380, 300]]} head={false} dashed />
      <Arrow points={[[380, 300], [30, 300]]} head={false} dashed />
      <Arrow points={[[30, 300], [30, 60], [340, 60], [340, 72]]} dashed />
      <Note x={36} y={196} lines={['refiled by a later sweep', '— reopens as OPEN']} />

      {/* The one verdict a later sweep does not overturn. */}
      <Arrow points={[[640, 112], [640, 150]]} dashed label="refiled: no change" labelAt={[652, 136]} labelAnchor="start" />
      <Node x={560} y={150} w={160} h={34} title="stays dismissed" tone="muted" dashed />

      <Note
        x={0}
        y={326}
        lines="A merge deletes a node, which makes the fingerprint unproducible — so a genuinely fixed card simply stops being flagged."
      />
    </Figure>
  );
}
